import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Camera, Users, Map, Calendar, Trash2, ArrowRight, Loader2, Ticket } from "lucide-react";
import { toast } from "sonner";

const ACTIONS = [
  { icon: Sparkles, label: "New Itinerary", to: "/planner", color: "bg-primary" },
  { icon: Camera, label: "Content Planner", to: "/content", color: "bg-accent" },
  { icon: Map, label: "Explore Map", to: "/explore", color: "bg-secondary" },
  { icon: Users, label: "Book a Local", to: "/services", color: "bg-primary" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [itineraries, setItineraries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [it, bk] = await Promise.all([api.get("/itineraries"), api.get("/bookings")]);
      setItineraries(it.data);
      setBookings(bk.data);
    } catch (e) {
      console.error("Failed to load dashboard data", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/itineraries/${id}`);
      setItineraries((p) => p.filter((i) => i.id !== id));
      toast.success("Itinerary removed");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-12 sm:px-10">
      <p className="overline text-primary">Your dashboard</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
        Salam, {user?.name?.split(" ")[0] || "traveler"} 👋
      </h1>
      <p className="mt-2 text-muted-foreground">Pick up where you left off or start something new.</p>

      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        {ACTIONS.map((a) => (
          <Link
            key={a.label}
            to={a.to}
            data-testid={`action-${a.to.slice(1)}`}
            className="group flex flex-col gap-4 rounded-2xl border bg-card p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)]"
          >
            <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${a.color} text-white`}>
              <a.icon className="h-5 w-5" />
            </span>
            <span className="font-medium">{a.label}</span>
          </Link>
        ))}
      </div>

      <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Itineraries */}
        <div className="lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold">Saved itineraries</h2>
            <Button asChild variant="ghost" size="sm" className="rounded-full">
              <Link to="/planner">New <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : itineraries.length === 0 ? (
            <div className="rounded-2xl border border-dashed bg-card/50 p-10 text-center" data-testid="empty-itineraries">
              <Sparkles className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 font-medium">No itineraries yet</p>
              <p className="mt-1 text-sm text-muted-foreground">Generate your first AI-crafted Morocco plan.</p>
              <Button asChild className="mt-5 rounded-full"><Link to="/planner">Create itinerary</Link></Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="itineraries-list">
              {itineraries.map((it) => (
                <div key={it.id} className="rounded-2xl border bg-card p-6 transition-shadow hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-xl font-semibold">{it.plan?.title || "Morocco trip"}</h3>
                      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{it.plan?.summary}</p>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full bg-muted px-3 py-1">{it.plan?.days?.length || 0} days</span>
                        {(it.input?.destinations || []).slice(0, 3).map((d) => (
                          <span key={d} className="rounded-full bg-secondary/10 px-3 py-1 text-secondary">{d}</span>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bookings */}
        <div>
          <h2 className="mb-4 font-display text-2xl font-bold">Booking requests</h2>
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : bookings.length === 0 ? (
            <div className="rounded-2xl border border-dashed bg-card/50 p-8 text-center" data-testid="empty-bookings">
              <Ticket className="mx-auto h-7 w-7 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">No booking requests yet.</p>
              <Button asChild variant="outline" className="mt-4 rounded-full"><Link to="/services">Find a local</Link></Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="bookings-list">
              {bookings.map((b) => (
                <div key={b.id} className="rounded-2xl border bg-card p-5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide text-secondary">{b.service_type}</span>
                    <span className="rounded-full bg-accent/20 px-2.5 py-0.5 text-xs font-medium capitalize text-accent-foreground">{b.status}</span>
                  </div>
                  <p className="mt-1 font-semibold">{b.service_name}</p>
                  {b.date && (
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Calendar className="h-3.5 w-3.5" /> {b.date}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
